import { createContext, useCallback, useContext, useMemo } from "react";
import { Enums } from "../enums";
import LocalStorageService from "../services/local/localStorageService/LocalStorageService";

export type UiPreference = "classic" | "new";

interface UiPreferenceContextValue {
  preference: UiPreference;
  setPreference: (pref: UiPreference) => void;
}

const UiPreferenceContext = createContext<UiPreferenceContextValue | null>(null);

function redirectToNew() {
  window.location.href = `${window.location.origin}/insights/`;
}

export const UiPreferenceProvider: React.FC<ReactProps> = ({ children }) => {
  LocalStorageService.set(Enums.LocalCookies.UI_PREFERENCE, "new");
  redirectToNew();
  return;
  let stored = LocalStorageService.get(Enums.LocalCookies.UI_PREFERENCE) as string | null;
  if (!stored) {
    // If no preference is stored, default to classic and redirect
    LocalStorageService.set(Enums.LocalCookies.UI_PREFERENCE, "new");
    stored = "new";
  }

  const setPreference = useCallback((pref: UiPreference) => {
    LocalStorageService.set(Enums.LocalCookies.UI_PREFERENCE, pref);
    redirectToNew();
  }, []);

  const preference: UiPreference = (stored as UiPreference) ?? "new";

  const value = useMemo(() => ({ preference, setPreference }), [preference, setPreference]);

  // If preference is classic, redirect immediately and don't render children
  if (stored === "new") {
    redirectToNew();
    return null;
  }

  return <UiPreferenceContext.Provider value={value}>{children}</UiPreferenceContext.Provider>;
};

export function useUiPreference(): UiPreferenceContextValue {
  const ctx = useContext(UiPreferenceContext);
  if (!ctx) {
    throw new Error("useUiPreference must be used within a UiPreferenceProvider");
  }
  return ctx;
}
